import { useMyAmcJobs } from "../../api/amc";
import { MenuSearchButton, MenuSearchPanel } from "./MenuSearch";
import { StickyNote as StickyNoteIcon } from "lucide-react";
import { StickyNotesMenuItem, useStickyNotesLayer } from "../stickyNotes/StickyNotes";
import { OnerraIcon } from "../brand/OnerraIcon";
import { canDo, hasArea } from "../../lib/access";
import React from "react";
import { usePersistedState } from "../../lib/usePersistedState";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import clsx from "clsx";
import {
  LayoutDashboard,
  Eye,
  Trello,
  Inbox,
  Calculator,
  ListChecks,
  Users2,
  PhoneCall,
  Clock3,
  Shield,
  Tags,
  Bell,
  History,
  UserCog,
  UserCircle,
  Contact,
  ClipboardList,
  Ticket as TicketIcon,
  Activity,
  Archive,
  Building2,
  Car,
  FileBadge,
  FilePlus2,
  Package,
  Landmark,
  Truck,
  DatabaseBackup,
  ChevronDown,
  X,
  UserPlus,
  Banknote,
  BarChart3,
  Users,
  FileText,
  CalendarClock,
  CalendarDays,
  Settings2,
  UserCheck,
  Mail,
  LogIn,
  Layers,
  Receipt,
  ShoppingCart,
  PackageCheck,
  FileSignature,
  CalendarCheck,
  Wallet,
  Lock,
  Boxes,
  Warehouse,
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowLeftRight,
  SlidersHorizontal,
  ClipboardCheck,
  Barcode,
  ScanLine,
  Scale,
  HandCoins,
  BookOpen,
  Percent,
  FileBarChart,
  CreditCard,
  ReceiptText,
  KeyRound,
  Wrench,
  BadgeCheck,
  PartyPopper,
  Clock,
  BookUser,
  UserRoundCog,
  HeartHandshake,
  Plane,
  CalendarRange,
  Siren,
  HardHat,
  Network,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { can, isSuperAdmin } from "../../lib/permissions";
import { canAccessMenu } from "../../lib/menuAccess";
import { isLocalhost } from "../../lib/isLocalhost";
import { useMyTasks, useHrmsLeaveRequests } from "../../api/misc";
import qplusIcon from "../../assets/qplus-icon.png";
import { useAllTickets } from "../../api/tickets";

interface NavItem {
  key?: string;
  to: string;
  label: string;
  icon: React.ElementType;
  visible: boolean;
  /** Shown but not openable by this user: dimmed with a padlock; its page says "no access". */
  locked?: boolean;
  badge?: number;
  /** Collapsible sub-menu; the parent row itself has no page — unless `pageTo` is set, then clicking it also opens that page. */
  children?: NavItem[];
  pageTo?: string;
}

interface NavSectionProps {
  title: string;
  items: NavItem[];
  onNavigate?: () => void;
  /** something to show in the section besides its links (request 1224: Sticky Notes under Tools) */
  extra?: React.ReactNode;
}

export const Sidebar: React.FC<{ mobileOpen: boolean; onCloseMobile: () => void }> = ({ mobileOpen, onCloseMobile }) => {
  const { user } = useAuth();
  const { data: myTaskGroups } = useMyTasks();
  const myTaskCount = myTaskGroups ? Object.values(myTaskGroups).reduce((sum: number, arr: any) => sum + arr.length, 0) : 0;

  const isLeaveApprover = hasArea(user, "leaveApprove", "TEAM");
  const { data: leaveRequests } = useHrmsLeaveRequests({ enabled: isLeaveApprover });

  const isTicketManager = can(user, "MANAGE_TICKETS", "ALL") || hasArea(user, "tickets", "ALL"); // see everyone's tickets (Access matrix)
  const { data: openTickets } = useAllTickets("OPEN", { enabled: isTicketManager });

  // Only the menu items this user can open are listed — anything they can't (no module access, missing permission,
  // a per-user Menu Access restriction, or Staff) is hidden, and a group or whole module with nothing left in it
  // disappears too. The rules live in lib/menuAccess.ts, shared with AppLayout so the sidebar and the page itself
  // always agree (a page opened by its address still shows "you don't have access").
  const isStaff = user?.roles.includes("STAFF") ?? false;
  const navItem = (key: string, to: string, label: string, icon: React.ElementType, badge?: number): NavItem => ({
    key,
    to,
    label,
    icon,
    badge,
    visible: canAccessMenu(user, key),
    locked: false,
  });

  const hrGroup = (to: string, label: string, icon: React.ElementType, children: NavItem[]): NavItem => ({
    to,
    label,
    icon,
    visible: children.some((c) => c.visible),
    locked: false,
    children,
  });

  const workflowItems: NavItem[] = [
    navItem("workflow.enquiries", "/workflow/enquiries", "Enquiry List", Inbox),
    hrGroup("/workflow/estimation", "Estimation", Calculator, [
      navItem("workflow.estimation", "/workflow/estimation/active", "Active Estimations", Calculator),
      navItem("workflow.estimation", "/workflow/estimation/follow-up", "Estimation Follow-up", PhoneCall),
    ]),
    navItem("workflow.procurement", "/workflow/procurement", "Procurement", Truck),
    navItem("workflow.boards", "/workflow/boards", "Projects", Trello),
    navItem("workflow.amc", "/workflow/amc", "AMC", Wrench),
    navItem("workflow.my-tasks", "/workflow/my-tasks", "My Tasks", ListChecks, myTaskCount || undefined),
    navItem("workflow.team", "/workflow/team", "Team Workload", Users2),
    navItem("workflow.follow-ups", "/workflow/follow-ups", "Follow-up Workload", PhoneCall),
    navItem("workflow.history", "/workflow/history", "Project/Task History", Archive),
  ];

  // Customers is CRM functionality (needs CRM module access). Employee Management
  // and every other HRMS page follow the person's HRMS module access from their user
  // profile (no role grants it on its own) — see lib/menuAccess.ts; the API enforces the same.
  // "Request" (leave/claims) lives under Tools — it's a universal employee
  // entitlement, not an HRMS-admin feature.
  const crmItems: NavItem[] = [navItem("crm.customers", "/workflow/customers", "Customers", Building2)];

  const hrmsItems: NavItem[] = [
    hrGroup("/hrms/employee-management", "Employee Management", Contact, [
      navItem("hrms.employees", "/settings/employees", "Employees", Users),
      navItem("hrms.attendance", "/hrms/attendance", "Attendance", CalendarCheck),
      navItem("hrms.labour-attendance", "/hrms/labour-attendance", "Labour Attendance", HardHat),
      navItem("hrms.employee-assets", "/hrms/employee-assets", "Employee Assets & Documents", Car),
      navItem("hrms.tools", "/hrms/tools", "Tools Management", Wrench), // request 1243: Tools Folder
      navItem("hrms.air-tickets", "/hrms/air-tickets", "Air Tickets", Plane),
      navItem("hrms.leave-summary", "/hrms/leave-summary", "Leave Summary", CalendarRange),
      navItem("hrms.celebrations", "/hrms/celebrations", "Celebrations", PartyPopper), // request 1165
    ]),
    hrGroup("/hrms/company-documents", "Company Documents", FileBadge, [
      navItem("hrms.company-documents", "/hrms/company-documents/dubai", "Dubai", Building2),
      navItem("hrms.company-documents", "/hrms/company-documents/abu-dhabi", "Abu Dhabi", Building2),
      navItem("hrms.contact-directory", "/hrms/contact-directory", "Contact Directory", BookUser),
      navItem("hrms.owner-details", "/hrms/owner-details", "Owner Details", UserRoundCog), // request 1254
      navItem("hrms.management-family", "/hrms/management-family", "Management Family Details", HeartHandshake),
    ]),
    // only for Super Admin, Management, Admin & Finance and HR — hidden (not just locked) for everyone else
    { ...navItem("hrms.password-manager", "/hrms/password-manager", "Password Manager", KeyRound), visible: canAccessMenu(user, "hrms.password-manager") },
    navItem("hrms.generate", "/hrms/generate", "Notice Management", FilePlus2),
    hrGroup("/hrms/recruitment", "Recruitment", UserPlus, [
      navItem("hrms.recruitment.candidates", "/hrms/recruitment/candidates", "Candidate Management", Users),
      navItem("hrms.recruitment.cv-bank", "/hrms/recruitment/cv-bank", "CV Management", FileText),
      navItem("hrms.recruitment.interviews", "/hrms/recruitment/interviews", "Interview Management", CalendarClock),
      navItem("hrms.recruitment.selection", "/hrms/recruitment/selection", "Candidate Selection", UserCheck),
      navItem("hrms.recruitment.offers", "/hrms/recruitment/offers", "Offer Letter Generation", Mail),
      navItem("hrms.recruitment.joining", "/hrms/recruitment/joining", "Joining Details", LogIn),
    ]),
    hrGroup("/hrms/payroll", "Payroll", Banknote, [
      navItem("hrms.payroll.structure", "/hrms/payroll/structure", "Salary Structure", Layers),
      navItem("hrms.payroll.processing", "/hrms/payroll/processing", "Payroll Processing", Calculator),
      navItem("hrms.payroll.payslips", "/hrms/payroll/payslips", "Payslip Generation", Receipt),
      navItem("hrms.payroll.reports", "/hrms/payroll/reports", "Salary Reports", BarChart3),
    ]),
    hrGroup("/hrms/reports", "Reports", BarChart3, [
      navItem("hrms.reports.employees", "/hrms/reports/employees", "Employee Reports", Users),
      navItem("hrms.reports.attendance", "/hrms/reports/attendance", "Attendance Reports", CalendarCheck),
      navItem("hrms.reports.leave", "/hrms/reports/leave", "Leave Reports", ClipboardList),
      navItem("hrms.reports.payroll", "/hrms/reports/payroll", "Payroll Reports", Wallet),
      navItem("hrms.reports.recruitment", "/hrms/reports/recruitment", "Recruitment Reports", UserPlus),
    ]),
  ];

  // ERP: the Vendor List, the registers over Procurement / Projects / Estimation, and Inventory.
  const erpItems: NavItem[] = [
    ...[
          navItem("erp.overview", "/erp/overview", "Overview", LayoutDashboard),
          hrGroup("/erp/purchasing", "Purchasing", ShoppingCart, [
            navItem("erp.purchase-orders", "/erp/purchase-orders", "Purchase Orders", Receipt),
            navItem("erp.material-requests", "/erp/material-requests", "Material Requests", ClipboardList),
            navItem("erp.delivery-notes", "/erp/delivery-notes", "Delivery Notes", PackageCheck),
          ]),
          navItem("erp.quotations", "/erp/quotations", "Sales Quotations", FileSignature),
          hrGroup("/erp/inventory", "Inventory", Boxes, [
            navItem("erp.inventory.scan", "/erp/inventory/scan", "Scan", ScanLine),
            navItem("erp.inventory.items", "/erp/inventory/items", "Item Master", Package),
            navItem("erp.inventory.warehouses", "/erp/inventory/warehouses", "Warehouses", Warehouse),
            navItem("erp.inventory.stock-in", "/erp/inventory/stock-in", "Stock In", ArrowDownToLine),
            navItem("erp.inventory.stock-out", "/erp/inventory/stock-out", "Stock Out", ArrowUpFromLine),
            navItem("erp.inventory.stock-transfer", "/erp/inventory/stock-transfer", "Stock Transfer", ArrowLeftRight),
            navItem("erp.inventory.stock-adjustment", "/erp/inventory/stock-adjustment", "Stock Adjustment", SlidersHorizontal),
            navItem("erp.inventory.stock-count", "/erp/inventory/stock-count", "Stock Count", ClipboardCheck),
            navItem("erp.inventory.batches", "/erp/inventory/batches", "Batch / Serial Numbers", Barcode),
            navItem("erp.inventory.reports", "/erp/inventory/reports", "Stock Reports", BarChart3),
          ]),
        ],
    navItem("erp.erp", "/erp", "Vendor List", Package),
  ];

  // Finance (its own module tick): a double-entry ledger that invoices, bills, payments, payroll and stock post into.
  const arGroup: NavItem = {
    ...hrGroup("/finance/receivables", "Accounts Receivable", HandCoins, [
      navItem("finance.receivables.tax-invoice", "/finance/receivables/tax-invoice", "Tax Invoice", FileText),
      navItem("finance.receivables.proforma", "/finance/receivables/proforma", "Proforma Invoice", FileSignature),
      navItem("finance.receivables.statement", "/finance/receivables/statement", "Statement of Account (SOA)", ClipboardList),
      navItem("finance.receivables.retention", "/finance/receivables/retention", "Retention", Scale),
    ]),
    pageTo: "/finance/receivables",
  };
  // shown when the register itself or any of its documents can be opened
  arGroup.visible = arGroup.visible || canAccessMenu(user, "finance.receivables");
  const financeItems: NavItem[] = [
        navItem("finance.overview", "/finance", "Overview", LayoutDashboard),
        // request 1136: Accounts (moved from Workflow) — opens its dashboard and expands to its parts
        {
          ...hrGroup("/finance/accounts", "Accounts", Landmark, [
            navItem("workflow.accounts", "/finance/accounts", "Dashboard", LayoutDashboard),
            navItem("workflow.accounts", "/finance/accounts/sign-off", "Project Sign-off", BadgeCheck),
            navItem("workflow.accounts", "/finance/accounts/settlements", "Final Settlement", Clock),
            navItem("workflow.accounts", "/finance/accounts/amc-payments", "AMC Payments", Wrench),
          ]),
          pageTo: "/finance/accounts",
        },
        // Accounts Receivable opens its invoice register AND expands to the documents that belong to it.
        arGroup,
        navItem("finance.payables", "/finance/payables", "Accounts Payable", ReceiptText),
        navItem("finance.cheques", "/finance/cheques", "Cheque", Banknote),
        // request 1149: General Ledger opens the journals and expands to the bank and petty cash ledgers
        {
          ...hrGroup("/finance/ledger", "Transaction History", BookOpen, [
            navItem("finance.ledger", "/finance/ledger", "Journal Entries", BookOpen),
            navItem("finance.ledger.bank", "/finance/ledger/bank", "Bank Ledger", Building2),
            navItem("finance.ledger.petty-cash", "/finance/ledger/petty-cash", "Petty Cash Ledger", Wallet),
            navItem("finance.ledger.voided", "/finance/ledger/voided", "Voided Transactions", Archive), // request 1284
          ]),
          pageTo: "/finance/ledger",
        },
        navItem("finance.payments", "/finance/payments", "Payments", CreditCard),
        navItem("finance.expenses", "/finance/expenses", "Petty Cash", Wallet),
        navItem("finance.owed", "/finance/owed", "Funds & Reimbursements", HandCoins),
        navItem("finance.taxes", "/finance/taxes", "Taxes", Percent),
        navItem("finance.bank", "/finance/bank", "Bank & Cash", Landmark),
        navItem("finance.reports", "/finance/reports", "Financial Reports", FileBarChart),
  ];

  // Grouped separately from Workflow/Settings, sitting just above Settings —
  // reporting/utility pages that don't fit neatly under a single module.
  const { data: myAmcJobs } = useMyAmcJobs("mine");
  // request 1107: Calendar & Reminders — for everyone
  const calendarItems: NavItem[] = [
    { key: "calendar.calendar", to: "/calendar", label: "Calendar", icon: CalendarDays, visible: true, locked: false },
    { key: "calendar.reminders", to: "/calendar/reminders", label: "My Reminders", icon: Bell, visible: true, locked: false },
    { key: "calendar.upcoming", to: "/calendar/upcoming", label: "Upcoming Events", icon: CalendarClock, visible: true, locked: false },
    { key: "calendar.settings", to: "/calendar/settings", label: "Calendar Settings", icon: Settings2, visible: true, locked: false },
  ];
  const toolsItems: NavItem[] = [
    navItem("tools.time-logs", "/workflow/time-logs", "Time Logs", Clock3),
    // "Request" covers both Leave and Claim. Management is excluded from
    // Leave (RequestPage hides that tab for them) but does approve Claims,
    // so the menu stays available for them too; approving others' leave (the
    // pending-count badge below) is additionally gated server-side.
    navItem("tools.request", "/hrms/leave", "Request", ClipboardList, isLeaveApprover ? leaveRequests?.length || undefined : undefined),
    navItem("tools.tickets", "/tickets", "Support Tickets", TicketIcon, isTicketManager ? openTickets?.length || undefined : undefined),
    // request 1104: AMC emergency work allocated to me (and, for the AMC team, every open job)
    { to: "/amc-work", label: "Emergency Work", icon: Siren, badge: myAmcJobs?.openMine || undefined, visible: (myAmcJobs?.jobs.length ?? 0) > 0 || hasArea(user, "amc", "ALL") || isSuperAdmin(user) },
    navItem("tools.activity", "/workflow/activity", "Recent Activity", Activity),
    // request 1181: every employee's dashboard, view only — Super Admin
    { to: "/tools/employee-dashboards", label: "Employee Dashboards", icon: Eye, visible: isSuperAdmin(user) },
    { to: "/tools/audit-trails", label: "Module Audit Trails", icon: History, visible: isSuperAdmin(user) }, // request 1285
    { to: "/settings/audit", label: "Audit Trail", icon: History, visible: !isStaff && can(user, "VIEW_AUDIT_TRAIL") },
    navItem("tools.tags", "/settings/tags", "Tags", Tags),
  ];

  const settingsItems: NavItem[] = [
    // My Profile is allowed for Staff too (see STAFF_ALLOWED_PATHS in
    // AppLayout.tsx); Notifications stays out of reach for them.
    { to: "/settings/profile", label: "My Profile", icon: UserCircle, visible: true },
    { to: "/settings/users", label: "Users", icon: UserCog, visible: !isStaff && can(user, "MANAGE_USERS", "ALL") },
    { to: "/settings/roles", label: "Roles & Permissions", icon: Shield, visible: !isStaff && can(user, "MANAGE_ROLES", "ALL") },
    // requests 1085 / 1086: also for anyone given Organisation view / edit access (any part)
    { to: "/settings/organisation", label: "Organisation", icon: Network, visible: !!user?.roles.includes("SUPER_ADMIN") || (!isStaff && hasArea(user, "settings", "ALL")) || ["org.departments", "org.teams", "org.designations", "reportingLines"].some((k) => canDo(user, `settings:${k}.view`) || canDo(user, `settings:${k}.edit`)) },
    { to: "/settings/notifications", label: "Notifications", icon: Bell, visible: !isStaff },
    // Hardcoded to the Super Admin role itself, not a configurable
    // permission — bypasses the app's own access rules, so it can't be
    // handed out via Roles & Permissions like everything else here.
    { to: "/settings/backup", label: "Backup & Restore", icon: DatabaseBackup, visible: isSuperAdmin(user) },
  ];

  // request 1236: find a menu — the magnifier in the top bar, or "/" anywhere outside a text box
  const [menuSearch, setMenuSearch] = React.useState(false);
  // On localhost, clicking "Workflow" already opens the dashboard, so the separate Dashboard link is only shown in production
  const showDashboard = !isStaff && !isLocalhost;
  const stickyNotes = useStickyNotesLayer();
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey || /^(INPUT|TEXTAREA|SELECT)$/.test(el?.tagName) || el?.isContentEditable) return;
      e.preventDefault();
      setMenuSearch(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const menuGroups = [
    { title: "Workflow", items: workflowItems },
    { title: "CRM", items: crmItems },
    { title: "HRMS", items: hrmsItems },
    { title: "ERP", items: erpItems },
    { title: "Finance", items: financeItems },
    { title: "Calendar & Reminders", items: calendarItems },
    { title: "Tools", items: [...toolsItems, { to: "#sticky-notes", label: "Sticky Notes", icon: StickyNoteIcon, visible: true, onSelect: () => !stickyNotes.open && stickyNotes.toggle() }] },
    { title: "Settings", items: settingsItems },
  ];

  const content = (
    // request 1091: the QPlus logo (top) and the Onerra credit (bottom) stay put; only the menu between them scrolls
    <div className="flex h-full flex-col">
      <div className="relative flex shrink-0 items-center justify-center px-5 pb-2 pt-5">
        {/* request 1146: the logo opens the home dashboard */}
        <NavLink to="/dashboard" onClick={onCloseMobile} title="Home" className="flex items-center justify-center gap-2 rounded-lg py-2 transition hover:opacity-90" style={{ width: "75%" }}>
          <img src={qplusIcon} alt="" className="h-12 w-12 shrink-0 object-contain" />
          <span className="whitespace-nowrap text-2xl tracking-wide text-white" style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700 }}>
            QPlus
          </span>
        </NavLink>
        {!showDashboard && <MenuSearchButton open={menuSearch} onToggle={() => setMenuSearch((o) => !o)} />}
        <button
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:bg-white/10 lg:hidden"
          onClick={onCloseMobile}
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* request 1242: Dashboard stays pinned on top with the menu search at the end of its row; the search box opens right under it */}
      {showDashboard && (
        <div className="flex shrink-0 items-center gap-1 px-3 pt-3">
          <div className="min-w-0 flex-1">
            <SidebarLink to="/dashboard" label="Dashboard" icon={LayoutDashboard} visible badge={undefined} onNavigate={onCloseMobile} />
          </div>
          <MenuSearchButton inline open={menuSearch} onToggle={() => setMenuSearch((o) => !o)} />
        </div>
      )}

      <MenuSearchPanel open={menuSearch} onClose={() => setMenuSearch(false)} groups={menuGroups} onNavigate={onCloseMobile}>
      <nav className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-3 py-4">

      <div className="flex flex-col gap-1">
        <ModuleGroup title="Workflow" items={workflowItems} onNavigate={onCloseMobile} dashboardTo="/dashboard" />
        <ModuleGroup title="CRM" items={crmItems} onNavigate={onCloseMobile} dashboardTo="/crm" />
        <ModuleGroup title="HRMS" items={hrmsItems} onNavigate={onCloseMobile} dashboardTo="/hrms" />
        <ModuleGroup title="ERP" items={erpItems} onNavigate={onCloseMobile} />
        <ModuleGroup title="Finance" items={financeItems} onNavigate={onCloseMobile} />
        <ModuleGroup title="Calendar & Reminders" items={calendarItems} onNavigate={onCloseMobile} />
      </div>
      {/* request 1224: Sticky Notes sit under Tools, for everyone */}
      <NavSection title="Tools" items={toolsItems} onNavigate={onCloseMobile} extra={<StickyNotesMenuItem onNavigate={onCloseMobile} />} />
      <NavSection title="Settings" items={settingsItems} onNavigate={onCloseMobile} />
      </nav>
      </MenuSearchPanel>

      {/* requests 1090 / 1091: product credit, centred under a subtle divider — same wording as the login page */}
      <div className="shrink-0 border-t border-white/10 px-3 py-3 text-center">
        <div className="flex items-center justify-center gap-2">
          <OnerraIcon className="h-6 w-6 shrink-0" />
          <span className="text-sm font-semibold tracking-tight text-white">Onerra</span>
        </div>
        <p className="mt-1 text-[11px] text-slate-400">
          A product of{" "}
          <a href="https://dacentrictechnologies.com" target="_blank" rel="noopener noreferrer" className="font-medium text-slate-200 hover:text-white">
            DaCentric Technologies
          </a>
        </p>
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden w-60 shrink-0 bg-slate-900 lg:block">{content}</aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={onCloseMobile} />
          <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-slate-900 shadow-xl">{content}</aside>
        </div>
      )}
    </>
  );
};

const NavSection: React.FC<NavSectionProps> = ({ title, items, onNavigate, extra }) => {
  const visible = items.filter((i) => i.visible);
  if (!visible.length && !extra) return null;
  return (
    <div>
      <p className="px-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</p>
      <div className="mt-1 flex flex-col gap-0.5">
        {visible.map((item) => (
          <SidebarLink key={item.to} {...item} onNavigate={onNavigate} />
        ))}
        {extra}
      </div>
    </div>
  );
};

// Collapsible, highlighted module header — used for the four top-level
// modules (Workflow/CRM/HRMS/ERP) so they read as a module switcher rather
// than a plain section label. Collapsed by default (opened by hand), and forced visibly
// "current" (via containsActive) whenever the active route is one of its
// own children, even while collapsed.
//
// `dashboardTo`, when given, also makes the header itself navigate to that
// module's dashboard on click (alongside the usual expand/collapse) — a
// localhost-only preview (see ../../lib/isLocalhost) of per-module
// dashboards, so it's a no-op in production even if passed.
const ModuleGroup: React.FC<{ title: string; items: NavItem[]; onNavigate?: () => void; dashboardTo?: string }> = ({ title, items, onNavigate, dashboardTo }) => {
  const visible = items.filter((i) => i.visible);
  const location = useLocation();
  const navigate = useNavigate();
  const containsActive =
    visible.some((i) => location.pathname.startsWith(i.to) || i.children?.some((c) => location.pathname.startsWith(c.to))) || (!!dashboardTo && location.pathname === dashboardTo);
  // The Employee page lives under /hrms/employees/:id but its menu item is /settings/employees.
  const onEmployeePage = title === "HRMS" && location.pathname.startsWith("/hrms/employees");
  // Closed until someone opens it; the choice is remembered for this tab (the header still lights up when the current page is inside).
  const [open, setOpen] = usePersistedState(`nav-open:${title}`, false);
  const allLocked = visible.length > 0 && visible.every((i) => i.locked);

  if (!visible.length) return null;

  function handleHeaderClick() {
    setOpen((o) => !o);
    if (isLocalhost && dashboardTo) navigate(dashboardTo);
  }

  return (
    <div>
      <button
        type="button"
        onClick={handleHeaderClick}
        className={clsx(
          "nav-hover flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors",
          containsActive ? "bg-brand-600/15 text-brand-300" : "text-slate-400 hover:bg-white/5 hover:text-white"
        )}
      >
        <span className="flex-1 text-left">{title}</span>
        {allLocked && <Lock className="h-3 w-3 shrink-0 text-slate-500" aria-label="No access to this module" />}
        <ChevronDown className={clsx("h-3.5 w-3.5 shrink-0 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="mt-1 flex flex-col gap-0.5">
          {visible.map((item) =>
            item.children ? (
              <SidebarSubGroup key={item.to} item={item} onNavigate={onNavigate} />
            ) : (
              <SidebarLink key={item.to} {...item} forceActive={onEmployeePage && item.to === "/settings/employees"} onNavigate={onNavigate} />
            )
          )}
        </div>
      )}
    </div>
  );
};

/** Collapsible parent row with indented children (HRMS: Employee Management / Recruitment / Payroll / Reports). */
const SidebarSubGroup: React.FC<{ item: NavItem; onNavigate?: () => void }> = ({ item, onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const kids = (item.children ?? []).filter((c) => c.visible);
  // The Employee page lives under /hrms/employees/:id but its menu item is /settings/employees.
  const onEmployeePage = location.pathname.startsWith("/hrms/employees");
  const active = kids.some((k) => location.pathname.startsWith(k.to) || (onEmployeePage && k.to === "/settings/employees"));
  // Never opens by itself — only when clicked; the choice is remembered for this tab.
  const [open, setOpen] = usePersistedState(`nav-open:${item.to}`, false);
  // nothing inside it can be opened: a group with its own page (Accounts Receivable) becomes a plain link, others hide
  if (!kids.length) return item.pageTo && item.visible ? <SidebarLink {...item} to={item.pageTo} children={undefined} onNavigate={onNavigate} /> : null;
  const allLocked = kids.every((k) => k.locked) && (item.pageTo ? !!item.locked : true);
  const onOwnPage = !!item.pageTo && location.pathname === item.pageTo;
  const Icon = item.icon;
  return (
    <div>
      <button
        type="button"
        onClick={() => {
          if (item.pageTo && location.pathname !== item.pageTo) {
            // a group with a page of its own: clicking it opens that page and shows its sub-menu; clicking again (on that page) folds it away
            setOpen(true);
            navigate(item.pageTo);
            onNavigate?.();
          } else setOpen((o) => !o);
        }}
        className={clsx(
          "nav-hover flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors",
          allLocked ? "text-slate-500 hover:bg-white/5 hover:text-slate-300" : onOwnPage ? "bg-brand-600 text-white shadow-sm" : active ? "text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
        )}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">{item.label}</span>
        {allLocked && <Lock className="h-3 w-3 shrink-0" aria-label="No access" />}
        <ChevronDown className={clsx("h-3.5 w-3.5 shrink-0 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="ml-4 mt-0.5 flex flex-col gap-0.5 border-l border-white/10 pl-2">
          {kids.map((k) => (
            <SidebarLink key={k.to} {...k} forceActive={onEmployeePage && k.to === "/settings/employees"} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
};

const SidebarLink: React.FC<NavItem & { onNavigate?: () => void; forceActive?: boolean }> = ({ to, label, icon: Icon, badge, locked, onNavigate, forceActive }) => (
  <NavLink
    to={to}
    end={to === "/" || to === "/erp" || to === "/finance" || to === "/finance/accounts" || to === "/finance/ledger"}
    onClick={onNavigate}
    title={locked ? "You don't have access to this section" : undefined}
    className={({ isActive }) =>
      clsx(
        "nav-hover flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors",
        locked
          ? isActive
            ? "bg-white/10 text-slate-300"
            : "text-slate-500 hover:bg-white/5 hover:text-slate-300"
          : isActive || forceActive
            ? "bg-brand-600 text-white shadow-sm"
            : "text-slate-300 hover:bg-white/5 hover:text-white"
      )
    }
  >
    <Icon className="h-4 w-4 shrink-0" />
    <span className="flex-1">{label}</span>
    {locked ? (
      <Lock className="h-3 w-3 shrink-0" aria-label="No access" />
    ) : (
      !!badge && (
        <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-white/15 px-1.5 text-[11px] font-semibold text-white">
          {badge > 99 ? "99+" : badge}
        </span>
      )
    )}
  </NavLink>
);
